import { Form, Formik } from 'formik'
import { MdDelete, MdDone, MdDragHandle } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Sheet, SheetCategory, Spend } from '../../../lib/Types'
import Button from '../../Formik/Button'
import Input from '../../Formik/Input'
import { useDeleteCategory, useUpdateCategory } from '../lib/Hooks/Categories'
import { useExpenses } from '../lib/Hooks/Spends'

type Props = {
  sheet: Sheet
  category: SheetCategory
}

const Category = ({ sheet, category }: Props) => {
  const { t } = useTranslation(['configuration', 'common'])
  const { data: spends } = useExpenses(sheet)
  const { mutateAsync: updateCategory, isPending: isUpdateLoading } =
    useUpdateCategory(sheet)
  const { mutateAsync: deleteCategory, isPending: isDeleteLoading } =
    useDeleteCategory(sheet)

  const isCategoryUsed = spends?.some(
    (spend: Spend) => spend.category.id === category.id,
  )

  return (
    <div className='draggable'>
      <Formik
        initialValues={{
          name: category.name || '',
          budget: category.budget || undefined,
        }}
        validationSchema={Yup.object({
          name: Yup.string().required(t('common:validation.required')),
        })}
        onSubmit={async (values, { resetForm }) => {
          await updateCategory({
            ...category,
            name: values.name,
            budget: values.budget,
          })
          resetForm({ values })
        }}
      >
        {({ dirty }) => (
          <Form>
            <div className={`flex mt-2 items-center ${!dirty ? 'mr-12' : ''}`}>
              <MdDragHandle className='draggable-handle mr-2 cursor-pointer' />
              <Input
                name='name'
                placeholder={t('configuration:category.namePlaceholder')}
                className='grow mr-2'
              />
              <Input
                type='number'
                name='budget'
                placeholder={t('configuration:category.budgetPlaceholder')}
                className='mr-2'
              />
              <Button
                tooltip={
                  isCategoryUsed
                    ? t('configuration:category.deleteDisabledTooltip')
                    : undefined
                }
                disabled={isCategoryUsed}
                type='button'
                loading={isDeleteLoading}
                theme='red'
                className='mr-2'
                onClick={() => {
                  if (
                    window.confirm(
                      t('configuration:category.confirmDelete', {
                        name: category.name,
                      }),
                    )
                  ) {
                    deleteCategory(category)
                  }
                }}
              >
                <MdDelete />
              </Button>
              {dirty && (
                <Button type='submit' loading={isUpdateLoading}>
                  <MdDone />
                </Button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default Category
